
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';

// project imports
import MainCard from 'components/MainCard';
import navigation from 'menu-items';

// assets
import ApartmentOutlined from '@ant-design/icons/ApartmentOutlined';
import HomeOutlined from '@ant-design/icons/HomeOutlined';
import HomeFilled from '@ant-design/icons/HomeFilled';

export default function Breadcrumbs({
  card = false,
  custom = false,
  divider = false,
  heading,
  icon,
  icons,
  links,
  maxItems,
  rightAlign,
  separator,
  title = true,
  titleBottom = true,
  sx,
  ...others
}) {
  const theme = useTheme();
  const location = useLocation();

  const [main, setMain] = useState();
  const [item, setItem] = useState();

  const iconSX = {
    marginRight: theme.spacing(0.75),
    marginLeft: 0,
    width: '1rem',
    height: '1rem',
    color: theme.vars.palette.secondary.main
  };

  let customLocation = location.pathname;

  // only used for component demo breadcrumbs
  if (customLocation.includes('/components-overview/breadcrumbs')) {
    customLocation = '/apps/customer/customer-card';
  }

  // ================= CARI MENU AKTIF =================
  useEffect(() => {
    // Bersihkan state halaman sebelumnya
    setMain(undefined);
    setItem(undefined);

    // Profile merupakan halaman khusus,
    // tidak terdaftar di menu-items
    if (customLocation === '/profile') {
      return;
    }

    const findMenu = (menus) => {
      if (!menus) return false;

      for (const menu of menus) {
        // Menu langsung
        if (menu.url === customLocation) {
          setMain(menu);
          setItem(menu);
          return true;
        }

        if (menu.children) {
          for (const child of menu.children) {
            // Item menu
            if (child.url === customLocation) {
              if (child.type === 'item') {
                setMain(menu);
                setItem(child);
              } else {
                setMain(child);
                setItem(child);
              }

              return true;
            }

            // Menu bertingkat
            if (child.children && findMenu([child])) {
              return true;
            }
          }
        }
      }

      return false;
    };

    findMenu(navigation?.items || []);

  }, [customLocation, custom]);

  // item separator
  const SeparatorIcon = separator;
  const separatorIcon = separator
    ? <SeparatorIcon style={{ fontSize: '0.75rem', marginTop: 2 }} />
    : '/';

  let mainContent;
  let itemContent;
  let breadcrumbContent = <Typography />;
  let itemTitle = '';
  let CollapseIcon;
  let ItemIcon;

  // ================= PROFILE BREADCRUMB =================
  if (customLocation === '/profile' && !custom) {
    return (
      <MainCard
        border={card}
        sx={
          card === false
            ? { mb: 3, bgcolor: 'inherit', backgroundImage: 'none', ...sx }
            : { mb: 3, ...sx }
        }
        {...others}
        content={card}
        shadow="none"
      >
        <Grid
          container
          direction={rightAlign ? 'row' : 'column'}
          spacing={1}
          sx={{
            justifyContent: rightAlign ? 'space-between' : 'flex-start',
            alignItems: rightAlign ? 'center' : 'flex-start'
          }}
        >
          <Grid>
            <MuiBreadcrumbs
              aria-label="breadcrumb"
              maxItems={maxItems || 8}
              separator={separatorIcon}
            >
              <Typography
                component={Link}
                to="/"
                color="text.secondary"
                variant="h6"
                sx={{ textDecoration: 'none' }}
              >
                {icons && <HomeOutlined style={iconSX} />}
                {icon && !icons && (
                  <HomeFilled style={{ ...iconSX, marginRight: 0 }} />
                )}
                {(!icon || icons) && 'Home'}
              </Typography>

              <Typography
                variant="subtitle1"
                color="text.primary"
              >
                View Profile
              </Typography>
            </MuiBreadcrumbs>
          </Grid>

          {title && (
            <Grid sx={{ mt: card === false ? 0.25 : 1 }}>
              <Typography variant="h2">
                View Profile
              </Typography>
            </Grid>
          )}
        </Grid>

        {card === false && divider !== false && (
          <Divider sx={{ mt: 2 }} />
        )}
      </MainCard>
    );
  }

  // ================= COLLAPSE ITEM =================
  if (main && main.type === 'collapse' && !main.breadcrumbs) {
    CollapseIcon = main.icon ? main.icon : ApartmentOutlined;

    mainContent = (
      <Typography
        {...(main.url && {
          component: Link,
          to: main.url
        })}
        variant={
          window.location.pathname === main.url
            ? 'subtitle1'
            : 'h6'
        }
        sx={{ textDecoration: 'none' }}
        color={
          window.location.pathname === main.url
            ? 'text.primary'
            : 'text.secondary'
        }
      >
        {icons && <CollapseIcon style={iconSX} />}
        {main?.title}
      </Typography>
    );

    if (!!custom) {
      breadcrumbContent = (
        <MainCard
          border={card}
          sx={
            card === false
              ? { mb: 3, bgcolor: 'inherit', backgroundImage: 'none', ...sx }
              : { mb: 3, ...sx }
          }
          {...others}
          content={card}
          shadow="none"
        >
          <Grid
            container
            direction={rightAlign ? 'row' : 'column'}
            spacing={1}
            sx={{
              justifyContent: rightAlign ? 'space-between' : 'flex-start',
              alignItems: rightAlign ? 'center' : 'flex-start'
            }}
          >
            <Grid>
              <MuiBreadcrumbs
                aria-label="breadcrumb"
                maxItems={maxItems || 8}
                separator={separatorIcon}
              >
                <Typography
                  component={Link}
                  to="/"
                  color="text.secondary"
                  variant="h6"
                  sx={{ textDecoration: 'none' }}
                >
                  {icons && <HomeOutlined style={iconSX} />}
                  {icon && !icons && (
                    <HomeFilled style={{ ...iconSX, marginRight: 0 }} />
                  )}
                  {(!icon || icons) && 'Home'}
                </Typography>

                {mainContent}
              </MuiBreadcrumbs>
            </Grid>

            {title && titleBottom && (
              <Grid sx={{ mt: card === false ? 0.25 : 1 }}>
                <Typography variant="h2">
                  {main.title}
                </Typography>
              </Grid>
            )}
          </Grid>

          {card === false && divider !== false && (
            <Divider sx={{ mt: 2 }} />
          )}
        </MainCard>
      );
    }
  }

  // ================= ITEMS =================
  if (
    (item && item.type === 'item') ||
    (item?.type === 'group' && item?.url) ||
    custom
  ) {
    itemTitle = item?.title;

    ItemIcon = item?.icon ? item.icon : ApartmentOutlined;

    itemContent = (
      <Typography
        variant="subtitle1"
        color="text.primary"
      >
        {icons && <ItemIcon style={iconSX} />}
        {itemTitle}
      </Typography>
    );

    let tempContent = (
      <MuiBreadcrumbs
        aria-label="breadcrumb"
        maxItems={maxItems || 8}
        separator={separatorIcon}
      >
        <Typography
          component={Link}
          to="/"
          color="text.secondary"
          variant="h6"
          sx={{ textDecoration: 'none' }}
        >
          {icons && <HomeOutlined style={iconSX} />}
          {icon && !icons && (
            <HomeFilled style={{ ...iconSX, marginRight: 0 }} />
          )}
          {(!icon || icons) && 'Home'}
        </Typography>

        {mainContent}
        {itemContent}
      </MuiBreadcrumbs>
    );

    if (custom && links && links?.length > 0) {
      tempContent = (
        <MuiBreadcrumbs
          aria-label="breadcrumb"
          maxItems={maxItems || 8}
          separator={separatorIcon}
        >
          {links?.map((link, index) => {
            CollapseIcon = link.icon
              ? link.icon
              : ApartmentOutlined;

            return (
              <Typography
                key={index}
                {...(link.to && {
                  component: Link,
                  to: link.to
                })}
                variant={!link.to ? 'subtitle1' : 'h6'}
                sx={{ textDecoration: 'none' }}
                color={!link.to ? 'text.primary' : 'text.secondary'}
              >
                {link.icon && <CollapseIcon style={iconSX} />}
                {link.title}
              </Typography>
            );
          })}
        </MuiBreadcrumbs>
      );
    }

    // main
    if (item?.breadcrumbs !== false || custom) {
      breadcrumbContent = (
        <MainCard
          border={card}
          sx={
            card === false
              ? { mb: 3, bgcolor: 'inherit', backgroundImage: 'none', ...sx }
              : { mb: 3, ...sx }
          }
          {...others}
          content={card}
          shadow="none"
        >
          <Grid
            container
            direction={rightAlign ? 'row' : 'column'}
            spacing={1}
            sx={{
              justifyContent: rightAlign ? 'space-between' : 'flex-start',
              alignItems: rightAlign ? 'center' : 'flex-start'
            }}
          >
            {title && !titleBottom && (
              <Grid>
                <Typography variant="h2">
                  {custom ? heading : item?.title}
                </Typography>
              </Grid>
            )}

            <Grid>{tempContent}</Grid>

            {title && titleBottom && (
              <Grid sx={{ mt: card === false ? 0.25 : 1 }}>
                <Typography variant="h2">
                  {custom ? heading : item?.title}
                </Typography>
              </Grid>
            )}
          </Grid>

          {card === false && divider !== false && (
            <Divider sx={{ mt: 2 }} />
          )}
        </MainCard>
      );
    }
  }

  return breadcrumbContent;
}

Breadcrumbs.propTypes = {
  card: PropTypes.bool,
  custom: PropTypes.bool,
  divider: PropTypes.bool,
  heading: PropTypes.string,
  icon: PropTypes.bool,
  icons: PropTypes.bool,
  links: PropTypes.array,
  maxItems: PropTypes.number,
  rightAlign: PropTypes.bool,
  separator: PropTypes.any,
  title: PropTypes.bool,
  titleBottom: PropTypes.bool,
  sx: PropTypes.any,
  others: PropTypes.any
};
